import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useArrivalDepartureTeaserData } from './hooks/useArrivalDepartureTeaserData';
import dayjs from 'dayjs';
import useLinks from '../../hooks/useLinks';

export interface ArrivalsDeparturesTeaserProps {
  show: boolean;
}
export interface BackgroundImageProps {
  url: string | null;
  show: boolean;
  onImageLoaded?: Function;
}

export interface TextProps {
  label: string;
  body: string;
  iconUrl: string;
  iconAltText: string;
}

// --------------------------------------
//  Background image
// --------------------------------------

const BackgroundImage = ({ url, show, onImageLoaded }: BackgroundImageProps) => {
  const imageFallbackUrl = '/Assets/images/hero_2880x1264_kajplatser.jpg';
  const [loaded, setLoaded] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  useEffect(() => {
    if (url && show) {
      setImageUrl(url);
    }
  }, [url, show]);

  useEffect(() => {
    if (imageUrl) {
      let image = new Image();
      image.src = imageUrl;

      image.onload = () => {
        setLoaded(true);
        if (onImageLoaded) {
          onImageLoaded();
        }
      };

      image.onerror = () => {
        setImageUrl(imageFallbackUrl);
      };
    }
  }, [imageUrl]);

  return imageUrl ? (
    <div
      className={`arrivals-departures-teaser-background ${loaded && show ? 'show' : ''}`}
      style={{ backgroundImage: `url(${imageUrl})` }}
    ></div>
  ) : null;
};

// --------------------------------------
//  Text
// --------------------------------------

const Text = ({ label, body, iconUrl, iconAltText }: TextProps) => (
  <div className="arrivals-departures-teaser-text-wrapper">
    <div className="arrivals-departures-teaser-icon">
      <img alt={iconAltText} src={iconUrl} />
    </div>
    <div className="arrivals-departures-teaser-text">
      <p className="label">{label}</p>
      <p className="body">{body.toLowerCase()}</p>
    </div>
  </div>
);

// --------------------------------------
//  ArrivalsDeparturesTeaser
// --------------------------------------

const ArrivalsDeparturesTeaser = ({ show }: ArrivalsDeparturesTeaserProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [locale, setLocale] = useState<string>('en');
  const [backgroundImageUrl, setBackgroundImageUrl] = useState<string | null>(null);
  const [backgroundLoaded, setBackgroundLoaded] = useState(false);
  const [linkUrl, setLinkUrl] = useState<string | null>(null);
  const [refreshData, setRefreshData] = useState(false);
  const [arrivalText, setArrivalText] = useState('-');
  const [departureText, setDepartureText] = useState('-');
  const { t } = useTranslation();

  let observer: IntersectionObserver;

  // ----------------------------------------------------------------------------
  //  useArrivalDepartureTeaserData hook - load the arrival and departure data
  // ----------------------------------------------------------------------------

  const { arrivalDepartureData } = useArrivalDepartureTeaserData(refreshData);

  // ----------------------------------------------------------------------------
  //  link
  // ----------------------------------------------------------------------------

  const { url, target } = useLinks(linkUrl);

  // ----------------------------------------------------------------------------
  //  useEffect - Mount
  // ----------------------------------------------------------------------------

  useEffect(() => {
    const e = document.getElementById('arrivalsDeparturesTeaser');
    if (document.documentElement.lang) {
      setLocale(document.documentElement.lang);
    }

    if (e?.dataset.link) {
      setLinkUrl(e.dataset.link);
    }

    if (e?.dataset.backgroundImage && e?.dataset.backgroundImage.length > 0) {
      setBackgroundImageUrl(e.dataset.backgroundImage);
    }

    setRefreshData(true);
  }, []);

  // ----------------------------------------------------------------------------
  //  useEffect - arrivalDepartureData
  // ----------------------------------------------------------------------------

  useEffect(() => {
    if (arrivalDepartureData) {
      setRefreshData(false);

      if (arrivalDepartureData.arrival && arrivalDepartureData.departure) {
        const arrivalDate = dayjs(arrivalDepartureData.arrival.arrivalTime).format('YYYY-MM-DD');
        const departureDate = dayjs(arrivalDepartureData.departure.departureTime).format('YYYY-MM-DD');

        const arrivalTime = dayjs(arrivalDepartureData.arrival.arrivalTime).format('HH:mm');
        const departureTime = dayjs(arrivalDepartureData.departure.departureTime).format('HH:mm');

        const arrivalString = `${arrivalTime} ${arrivalDepartureData.arrival.name}`;
        const departureString = `${departureTime} ${arrivalDepartureData.departure.name}`;

        setArrivalText(arrivalString);
        setDepartureText(departureString);
      }
    }
  }, [arrivalDepartureData]);

  const navigateToUrl = () => {
    if (url && target) {
      window.open(url, target);
    }
  };

  // ----------------------------------------------------------------------------
  //  JSX
  // ----------------------------------------------------------------------------

  return (
    <div
      ref={ref}
      className={`arrivals-departures-teaser ${linkUrl ? 'clickable' : ''}`}
      onClick={navigateToUrl}
      onKeyDown={(e) => (e.key === 'Enter' ? navigateToUrl() : null)}
      tabIndex={linkUrl ? 0 : -1}
    >
      <div className={`arrivals-departures-teaser-content`}>
        <Text
          label={`${t('arrivalsdepartures.nextArrival')}`}
          body={arrivalText}
          iconUrl="/Assets/icons/Ikon_40_ShipProfile_arrival.svg"
          iconAltText="Arrival icon"
        />
        <Text
          label={`${t('arrivalsdepartures.nextDeparture')}`}
          body={departureText}
          iconUrl="/Assets/icons/Ikon_40_ShipProfile_departure.svg"
          iconAltText="Departure icon"
        />
      </div>
      <div
        className={`arrivals-departures-teaser-overlay`}
        style={{ background: 'linear-gradient(110deg, #103558 70%, #00000000 70%)' }}
      ></div>
      <BackgroundImage url={backgroundImageUrl} onImageLoaded={() => setBackgroundLoaded(true)} show />
    </div>
  );
};

export default ArrivalsDeparturesTeaser;
